/**
 * Touch controls (Bedrock-style): left joystick, jump / sneak buttons, drag to
 * look, tap to place / attack, hold to mine or use, plus top-bar buttons.
 * Everything feeds the existing input paths with synthetic (untrusted) key and
 * mouse events, so the game logic is the desktop logic. The fake pointer lock
 * lives in input/touchEnv. Pure maths: input/touchMath.
 */

import { onTouchEnabled, touchEnabled } from '../input/touchEnv';
import { classifyGesture, lookDelta, stickKeys, stickSprints, stickVector } from '../input/touchMath';

export { touchEnabled };

export interface TouchDeps {
  canvas: HTMLCanvasElement;
  player: { yaw: number; pitch: number; sensitivity: number; dead: boolean };
  /** Game running with nothing on top (no menu, panel or death screen). */
  isPlaying: () => boolean;
  /** A container / trade / teleport panel is up: show the close button. */
  panelOpen: () => boolean;
  /** What the crosshair is on: a tap attacks mobs and places / uses otherwise. */
  targetKind: () => 'mob' | 'other';
  /** Held item is used by holding (bow, food, shield...), not mined with. */
  holdUses: () => boolean;
  /** The container screens' stage element (touch is translated to mouse there). */
  screenStage: HTMLElement;
}

const CSS = `
html,body{overscroll-behavior:none;touch-action:none;-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;-webkit-tap-highlight-color:transparent}
canvas{touch-action:none}
.tc-root{position:fixed;inset:0;z-index:30;pointer-events:none;touch-action:none;font-family:monospace}
.tc-root *{touch-action:none;-webkit-user-select:none;user-select:none}
.tc-btn{position:absolute;pointer-events:auto;display:flex;align-items:center;justify-content:center;border-radius:50%;
  background:rgba(40,40,44,.45);border:2px solid rgba(255,255,255,.35);color:#fff;font:700 13px monospace;text-shadow:1px 1px 0 #000}
.tc-btn.sq{border-radius:6px}
.tc-btn.on,.tc-btn:active{background:rgba(255,255,255,.4)}
.tc-stick{position:absolute;pointer-events:auto;border-radius:50%;background:rgba(40,40,44,.3);border:2px solid rgba(255,255,255,.3)}
.tc-knob{position:absolute;left:50%;top:50%;width:52px;height:52px;margin:-26px 0 0 -26px;border-radius:50%;background:rgba(255,255,255,.45);border:2px solid rgba(255,255,255,.7)}
.tc-close{position:fixed;z-index:70;right:calc(12px + env(safe-area-inset-right));top:calc(12px + env(safe-area-inset-top));width:48px;height:48px;display:none;
  align-items:center;justify-content:center;background:#c6c6c6;border:3px solid #000;box-shadow:inset 3px 3px 0 #fff,inset -3px -3px 0 #555;color:#222;font:700 24px monospace}
.tc-rotate{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.88);color:#fff;display:none;flex-direction:column;align-items:center;justify-content:center;
  font:700 18px monospace;text-align:center;gap:14px;padding:24px}
.tc-rotate small{font-weight:400;opacity:.7;font-size:13px}
`;

type Code = string;

export function initTouchControls(deps: TouchDeps): void {
  onTouchEnabled(() => build(deps));
}

function build(deps: TouchDeps): void {
  const { canvas, player } = deps;
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  const root = document.createElement('div');
  root.className = 'tc-root';
  document.body.appendChild(root);
  const SAFE_L = 'env(safe-area-inset-left)', SAFE_R = 'env(safe-area-inset-right)', SAFE_B = 'env(safe-area-inset-bottom)', SAFE_T = 'env(safe-area-inset-top)';

  // --- synthetic input -------------------------------------------------------
  const down = new Set<Code>();
  const key = (code: Code, on: boolean): void => {
    if (on === down.has(code)) return;
    if (on) down.add(code); else down.delete(code);
    document.dispatchEvent(new KeyboardEvent(on ? 'keydown' : 'keyup', { code, bubbles: true, cancelable: true }));
  };
  const tapKey = (code: Code): void => {
    document.dispatchEvent(new KeyboardEvent('keydown', { code, bubbles: true, cancelable: true }));
    document.dispatchEvent(new KeyboardEvent('keyup', { code, bubbles: true, cancelable: true }));
  };
  const mouse = (type: 'mousedown' | 'mouseup', button: number): void => {
    document.dispatchEvent(new MouseEvent(type, { button, bubbles: true, cancelable: true }));
  };

  /** One finger = one control: callbacks only see their own touch identifier. */
  function track(el: HTMLElement, h: { start: (t: Touch) => void; move: (t: Touch) => void; end: (t: Touch, cancelled: boolean) => void }): void {
    let id = -1;
    const find = (list: TouchList): Touch | null => { for (let i = 0; i < list.length; i++) if (list[i].identifier === id) return list[i]; return null; };
    el.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (id !== -1) return;
      const t = e.changedTouches[0];
      id = t.identifier;
      h.start(t);
    }, { passive: false });
    el.addEventListener('touchmove', (e) => { const t = find(e.changedTouches); if (t) { e.preventDefault(); h.move(t); } }, { passive: false });
    const fin = (cancelled: boolean) => (e: TouchEvent): void => {
      const t = find(e.changedTouches);
      if (!t) return;
      e.preventDefault();
      id = -1;
      h.end(t, cancelled);
    };
    el.addEventListener('touchend', fin(false), { passive: false });
    el.addEventListener('touchcancel', fin(true), { passive: false });
  }

  const button = (label: string, css: string, cls = ''): HTMLDivElement => {
    const b = document.createElement('div');
    b.className = 'tc-btn ' + cls;
    b.style.cssText = css;
    b.textContent = label;
    root.appendChild(b);
    return b;
  };
  const press = (b: HTMLElement, onDown: () => void, onUp?: () => void): void => {
    track(b, { start: () => { b.classList.add('on'); onDown(); }, move: () => {}, end: () => { b.classList.remove('on'); onUp?.(); } });
  };

  // --- joystick (left) -------------------------------------------------------
  const R = 62;
  const stick = document.createElement('div');
  stick.className = 'tc-stick';
  stick.style.cssText = `width:${R * 2}px;height:${R * 2}px;left:calc(28px + ${SAFE_L});bottom:calc(78px + ${SAFE_B})`;
  const knob = document.createElement('div');
  knob.className = 'tc-knob';
  stick.appendChild(knob);
  root.appendChild(stick);
  const applyStick = (dx: number, dy: number): void => {
    const v = stickVector(dx, dy, R);
    const k = stickKeys(v);
    key('KeyW', k.w); key('KeyS', k.s); key('KeyA', k.a); key('KeyD', k.d);
    key('ControlLeft', k.w && stickSprints(v));
    const m = Math.min(R, Math.hypot(dx, dy)) || 0;
    const a = Math.atan2(dy, dx);
    knob.style.transform = `translate(${Math.cos(a) * m}px,${Math.sin(a) * m}px)`;
  };
  const stickRel = (t: Touch): [number, number] => {
    const r = stick.getBoundingClientRect();
    return [t.clientX - (r.left + r.width / 2), t.clientY - (r.top + r.height / 2)];
  };
  track(stick, {
    start: (t) => applyStick(...stickRel(t)),
    move: (t) => applyStick(...stickRel(t)),
    end: () => applyStick(0, 0),
  });

  // --- right-hand buttons ----------------------------------------------------
  const jump = button('JUMP', `width:84px;height:84px;right:calc(28px + ${SAFE_R});bottom:calc(78px + ${SAFE_B})`);
  press(jump, () => key('Space', true), () => key('Space', false));
  const sneak = button('SNEAK', `width:64px;height:64px;right:calc(124px + ${SAFE_R});bottom:calc(84px + ${SAFE_B})`);
  let sneaking = false;
  press(sneak, () => { sneaking = !sneaking; key('ShiftLeft', sneaking); sneak.style.background = sneaking ? 'rgba(255,255,255,.4)' : ''; });

  // --- top bar ---------------------------------------------------------------
  const topBtn = (label: string, slot: number, code: Code): HTMLDivElement => {
    const b = button(label, `width:46px;height:46px;top:calc(10px + ${SAFE_T});right:calc(${10 + slot * 54}px + ${SAFE_R})`, 'sq');
    press(b, () => tapKey(code));
    return b;
  };
  topBtn('II', 0, 'Escape');
  topBtn('INV', 1, 'KeyE');
  topBtn('CHAT', 2, 'KeyT');
  topBtn('DROP', 3, 'KeyQ');
  topBtn('CAM', 4, 'KeyV');

  // --- look / tap / hold on the world view -----------------------------------
  interface Gesture { id: number; x0: number; y0: number; lx: number; ly: number; t0: number; holding: number; moved: number; timer: number }
  let g: Gesture | null = null; // holding: 0 none, 1 mining (button 0), 2 using (button 2)
  const endHold = (): void => {
    if (!g) return;
    if (g.holding === 1) mouse('mouseup', 0);
    else if (g.holding === 2) mouse('mouseup', 2);
    g.holding = 0;
  };
  const decide = (released: boolean): void => {
    if (!g) return;
    const now = performance.now();
    const state = classifyGesture(now - g.t0, g.moved, released, g.holding !== 0);
    if (state === 'hold' && g.holding === 0) {
      g.holding = deps.holdUses() ? 2 : 1;
      mouse('mousedown', g.holding === 2 ? 2 : 0);
    } else if (state === 'drag') endHold();
    else if (state === 'tap') {
      if (deps.targetKind() === 'mob') { mouse('mousedown', 0); mouse('mouseup', 0); }
      else { mouse('mousedown', 2); mouse('mouseup', 2); }
    } else if (state === 'release') endHold();
  };
  const arm = (): void => { if (g) g.timer = window.setTimeout(() => decide(false), 290); };
  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault();
    if (!deps.isPlaying() || g) return;
    const t = e.changedTouches[0];
    g = { id: t.identifier, x0: t.clientX, y0: t.clientY, lx: t.clientX, ly: t.clientY, t0: performance.now(), holding: 0, moved: 0, timer: 0 };
    arm();
  }, { passive: false });
  canvas.addEventListener('touchmove', (e) => {
    if (!g) return;
    let t: Touch | null = null;
    for (let i = 0; i < e.changedTouches.length; i++) if (e.changedTouches[i].identifier === g.id) t = e.changedTouches[i];
    if (!t) return;
    e.preventDefault();
    g.moved = Math.hypot(t.clientX - g.x0, t.clientY - g.y0);
    decide(false);
    if (g.holding === 0 && g.moved > 10 && !player.dead) {
      const d = lookDelta(t.clientX - g.lx, t.clientY - g.ly, player.sensitivity);
      player.yaw += d.yaw;
      player.pitch = Math.max(-Math.PI / 2 + 0.001, Math.min(Math.PI / 2 - 0.001, player.pitch + d.pitch));
    }
    g.lx = t.clientX; g.ly = t.clientY;
  }, { passive: false });
  const finish = (e: TouchEvent): void => {
    if (!g) return;
    let t: Touch | null = null;
    for (let i = 0; i < e.changedTouches.length; i++) if (e.changedTouches[i].identifier === g.id) t = e.changedTouches[i];
    if (!t) return;
    e.preventDefault();
    clearTimeout(g.timer);
    if (e.type === 'touchcancel') endHold(); else decide(true);
    g = null;
  };
  canvas.addEventListener('touchend', finish, { passive: false });
  canvas.addEventListener('touchcancel', finish, { passive: false });

  // --- real compat mouse events must not mine / place while playing ----------
  // (a tap on the hotbar, say, would otherwise reach the game's mousedown)
  for (const ev of ['mousedown', 'mouseup'] as const) {
    window.addEventListener(ev, (e) => { if (e.isTrusted && deps.isPlaying()) e.stopImmediatePropagation(); }, true);
  }

  // --- container screens: touch -> mouse (long press = right click) ----------
  const stage = deps.screenStage;
  const at = (x: number, y: number): Element => document.elementFromPoint(x, y) ?? stage;
  const ptr = (type: string, x: number, y: number, button: number, target: EventTarget): void => {
    target.dispatchEvent(new MouseEvent(type, { clientX: x, clientY: y, button, bubbles: true, cancelable: true }));
  };
  let sg: { id: number; x0: number; y0: number; t0: number; dragging: boolean; long: boolean; timer: number; ids: number } | null = null;
  let lastY2 = 0;
  stage.addEventListener('touchstart', (e) => {
    const tg = e.target as HTMLElement;
    if (tg instanceof HTMLInputElement || tg instanceof HTMLTextAreaElement) return; // let the creative search focus
    e.preventDefault();
    if (e.touches.length >= 2) { // two fingers scroll lists (creative palette)
      if (sg) { clearTimeout(sg.timer); if (sg.dragging) ptr('mouseup', sg.x0, sg.y0, 0, window); sg = null; }
      lastY2 = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      return;
    }
    const t = e.changedTouches[0];
    ptr('mousemove', t.clientX, t.clientY, 0, window);
    sg = { id: t.identifier, x0: t.clientX, y0: t.clientY, t0: performance.now(), dragging: false, long: false, timer: 0, ids: 1 };
    sg.timer = window.setTimeout(() => {
      if (!sg || sg.dragging) return;
      sg.long = true;
      const tgt = at(sg.x0, sg.y0);
      ptr('mousedown', sg.x0, sg.y0, 2, tgt); ptr('mouseup', sg.x0, sg.y0, 2, window);
    }, 380);
  }, { passive: false });
  stage.addEventListener('touchmove', (e) => {
    e.preventDefault();
    if (e.touches.length >= 2) {
      const y = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      stage.dispatchEvent(new WheelEvent('wheel', { deltaY: (lastY2 - y) * 2, bubbles: true, cancelable: true }));
      lastY2 = y;
      return;
    }
    if (!sg) return;
    const t = e.changedTouches[0];
    if (t.identifier !== sg.id || sg.long) return;
    if (!sg.dragging && Math.hypot(t.clientX - sg.x0, t.clientY - sg.y0) > 10) {
      clearTimeout(sg.timer);
      sg.dragging = true; // finger-drag across slots: spread / drag-split
      ptr('mousedown', sg.x0, sg.y0, 0, at(sg.x0, sg.y0));
    }
    if (sg.dragging) ptr('mousemove', t.clientX, t.clientY, 0, window);
  }, { passive: false });
  const sEnd = (e: TouchEvent): void => {
    e.preventDefault();
    if (!sg) return;
    const t = Array.from(e.changedTouches).find((c) => c.identifier === sg!.id);
    if (!t) return;
    clearTimeout(sg.timer);
    if (sg.dragging) ptr('mouseup', t.clientX, t.clientY, 0, window);
    else if (!sg.long) {
      ptr('mousedown', sg.x0, sg.y0, 0, at(sg.x0, sg.y0));
      ptr('mouseup', sg.x0, sg.y0, 0, window);
      // the host's own double-click gather works on the next tap (250 ms window)
    }
    sg = null;
  };
  stage.addEventListener('touchend', sEnd, { passive: false });
  stage.addEventListener('touchcancel', sEnd, { passive: false });

  // --- close button for panels -----------------------------------------------
  const close = document.createElement('div');
  close.className = 'tc-close';
  close.textContent = 'X';
  document.body.appendChild(close);
  track(close, { start: () => {}, move: () => {}, end: () => tapKey('Escape') });

  // --- rotate hint (portrait) ------------------------------------------------
  const rotate = document.createElement('div');
  rotate.className = 'tc-rotate';
  rotate.innerHTML = '<div style="font-size:42px">&#8635;</div><div>Rotate your device</div><small>Landscape works best. Tap to continue anyway.</small>';
  document.body.appendChild(rotate);
  let dismissed = false;
  rotate.addEventListener('touchend', (e) => { e.preventDefault(); dismissed = true; rotate.style.display = 'none'; });
  rotate.addEventListener('click', () => { dismissed = true; rotate.style.display = 'none'; });

  // --- first tap: fullscreen + landscape lock where the browser allows -------
  window.addEventListener('touchend', () => {
    try {
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        void document.documentElement.requestFullscreen({ navigationUI: 'hide' }).then(
          () => (screen.orientation as unknown as { lock?: (o: string) => Promise<void> }).lock?.('landscape').catch(() => {}),
          () => {},
        );
      }
    } catch { /* unsupported (iOS Safari) */ }
  }, { once: true, passive: true });

  // --- visibility -------------------------------------------------------------
  let shown = true;
  const sync = (): void => {
    const play = deps.isPlaying();
    const panel = deps.panelOpen();
    if (play !== shown) {
      shown = play;
      root.style.display = play ? '' : 'none';
      if (!play) {
        applyStick(0, 0);
        key('Space', false);
        endHold();
        if (g) { clearTimeout(g.timer); g = null; }
      }
    }
    close.style.display = panel ? 'flex' : 'none';
    if (!dismissed) rotate.style.display = window.innerHeight > window.innerWidth ? 'flex' : 'none';
    requestAnimationFrame(sync);
  };
  window.addEventListener('orientationchange', () => { dismissed = false; });
  requestAnimationFrame(sync);
}
